import { Box, Button, Divider, Tooltip, Typography } from "@mui/material";
import { CheckoutContext } from "contexts/Checkout";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { AddOnItemName } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans, QUARTERLY_BASE_MONTHLY_PLAN } from "schemas/payments";

import { B2GO_PROMO_END_LABEL_WITH_YEAR } from "constants/influencer";
import { trackEvent } from "utils/tracking";

import styles from "../styles";
import { useCheckout } from "../useCheckout";
import BillingCycleSwitch from "./BillingCycleSwitch";
import CrossSells from "./CrossSells";
import NotInterestedButton from "./NotInterestedButton";
import summaryStyles, { summaryColors as colors } from "./orderSummaryStyles";

interface Props {
  details?: Map;
  handleBack: () => void;
  noBackButton?: boolean;
  trackSubscription: () => void;
}

const PRIMARY_DARK = "#151F16";

const money = (n: number) => "$" + n.toFixed(2);

const offerEndsLabel = B2GO_PROMO_END_LABEL_WITH_YEAR;

const B2GOCheckoutSummary = ({
  details,
  handleBack,
  noBackButton = false,
  trackSubscription,
}: Props) => {
  const { invoice, customer, splashPageSource, plan, applyAddOnLoading } =
    useContext(CheckoutContext);
  const { plans } = useContext(SubscriptionContext);
  const { applyAddOnItem } = useCheckout();

  const displayName = details?.display_name;

  const addOnLines = (invoice?.lines?.data || []).filter(
    (line) => line?.metadata?.item_name,
  );
  const addOnsTotal = addOnLines.reduce(
    (sum, line) => sum + Number(line.amount || 0) / 100,
    0,
  );
  // Proration credits from switching plans (e.g. "Unused time on Pro Plan after X")
  // are baked into amount_due but otherwise invisible in this synthesized breakdown.
  // They surface as negative lines with no add-on item_name.
  const prorationCreditLines = (invoice?.lines?.data || []).filter(
    (line) => !line?.metadata?.item_name && Number(line.amount || 0) < 0,
  );
  const totalDue = Number((invoice?.amount_due || 0) / 100);

  // Derive the plan's regular monthly rate from config so the deal math stays
  // correct regardless of which add-ons are bundled into the invoice total.
  const baseMonthlyPlan = plan
    ? QUARTERLY_BASE_MONTHLY_PLAN[plan as Plans]
    : undefined;
  const planConfigPrice = baseMonthlyPlan
    ? plans?.[baseMonthlyPlan]?.price
    : undefined;
  const monthlyBase =
    planConfigPrice ?? Math.max(0, (totalDue - addOnsTotal) / 2);

  const taxAmount = Number(invoice?.total_taxes?.[0]?.amount || 0) / 100;
  const lineBase = monthlyBase * 3;
  const discount = monthlyBase;
  const total = totalDue || monthlyBase * 2 + addOnsTotal;
  const preTaxTotal = total - taxAmount;
  const wasPrice = lineBase + addOnsTotal;
  const saved = Math.max(0, wasPrice - preTaxTotal);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        {!noBackButton ? (
          <Button
            onClick={handleBack}
            sx={{ textTransform: "none", color: colors.body, pl: 0 }}
          >
            <Box
              component="i"
              className="fa-regular fa-arrow-left"
              sx={{ mr: 1 }}
            />{" "}
            Back
          </Button>
        ) : (
          <Box />
        )}
        {splashPageSource && (
          <NotInterestedButton trackSubscription={trackSubscription} />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.75,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: 30, md: 38 },
            lineHeight: 1.05,
            color: colors.body,
          }}
        >
          {displayName}
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            backgroundColor: colors.yellow,
            color: PRIMARY_DARK,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.04em",
            px: 1.5,
            py: 0.75,
            borderRadius: "999px",
          }}
        >
          <Box component="i" className="fa-solid fa-gift" /> Buy 2, Get 1 Free
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        <BillingCycleSwitch />
      </Box>

      <Box
        sx={{
          mt: 3.25,
          backgroundColor: colors.cream,
          borderRadius: "16px",
          p: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.875,
            mb: 1.75,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: colors.primaryDeep,
          }}
        >
          <Box component="i" className="fa-solid fa-sparkles" /> Here's how your
          deal breaks down
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1.25,
          }}
        >
          {[
            { n: "Month 1", free: false },
            { n: "Month 2", free: false },
            { n: "Month 3", free: true },
          ].map((m) => (
            <Box
              key={m.n}
              sx={{
                borderRadius: "8px",
                px: 1.5,
                py: 1.75,
                textAlign: "center",
                backgroundColor: m.free ? colors.primary : "#fff",
                border: `1px solid ${m.free ? colors.primary : colors.border}`,
              }}
            >
              <Box
                sx={{
                  fontSize: 12,
                  color: m.free ? "rgba(247,238,111,0.85)" : colors.secondary,
                }}
              >
                {m.n}
              </Box>
              <Box
                sx={{
                  mt: 0.75,
                  fontWeight: 600,
                  fontSize: 13,
                  color: m.free ? colors.yellow : colors.body,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.625,
                }}
              >
                {m.free ? (
                  <>
                    <Box component="i" className="fa-solid fa-gift" /> Free
                  </>
                ) : (
                  "Pay now"
                )}
              </Box>
            </Box>
          ))}
        </Box>
        <Typography
          sx={{
            mt: 1.75,
            fontSize: 13,
            lineHeight: 1.5,
            color: colors.secondary,
          }}
        >
          You pay for <b>2 months today</b> and your <b>3rd month is on us</b> —
          a full month of {displayName} free.
        </Typography>
      </Box>

      <Box sx={{ mt: 3.75 }}>
        <Box sx={summaryStyles.line}>
          <Box sx={summaryStyles.lineLabel}>
            <Typography sx={summaryStyles.lineTitle}>
              {displayName} — 3 months
            </Typography>
            <Typography sx={summaryStyles.lineSub}>
              {money(monthlyBase)}/mo · one-time 3-month offer
            </Typography>
          </Box>
          <Typography sx={summaryStyles.lineAmount}>
            {money(lineBase)}
          </Typography>
        </Box>

        <Box sx={summaryStyles.line}>
          <Box sx={summaryStyles.promoChip}>
            <Box component="i" className="fa-solid fa-gift" /> B2GO · 1 month
            free
          </Box>
          <Typography sx={summaryStyles.lineAmountNeg}>
            −{money(discount)}
          </Typography>
        </Box>

        {prorationCreditLines.map((line, index) => (
          <Box key={`proration-${index}`} sx={summaryStyles.line}>
            <Typography sx={summaryStyles.lineTitle}>
              {line.description}
            </Typography>
            <Typography sx={summaryStyles.lineAmountNeg}>
              −{money(Math.abs(Number(line.amount || 0) / 100))}
            </Typography>
          </Box>
        ))}

        {addOnLines.map((line, index) => {
          const itemName = line?.metadata?.item_name;
          return (
            <Box
              key={index}
              sx={[summaryStyles.line, { alignItems: "flex-start" }]}
            >
              <Box sx={summaryStyles.lineLabel}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={summaryStyles.lineTitle}>
                    {line.description?.replace(/^1 × /, "")}
                  </Typography>
                  {itemName && (
                    <Tooltip title="Remove add-on" placement="right">
                      <Box
                        component="span"
                        onClick={async (e) => {
                          e.stopPropagation();
                          trackEvent(
                            "Invoice Preview - Remove Add-On Clicked",
                            {
                              "Add On Item Name": itemName,
                            },
                          );
                          await applyAddOnItem({
                            name: itemName as AddOnItemName,
                            quantity: 0,
                          });
                        }}
                        sx={styles.deleteAddOnButton(
                          applyAddOnLoading === itemName,
                        )}
                      >
                        <Box
                          component="i"
                          className={
                            applyAddOnLoading === itemName
                              ? "fa-regular fa-spinner-third fa-spin"
                              : "fa-regular fa-xmark"
                          }
                          sx={{ fontSize: 9 }}
                        />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
                {itemName === AddOnItemName.customer_support && (
                  <Typography sx={summaryStyles.lineSub}>
                    Get a personal review from our team to make sure your
                    account is set up properly.
                  </Typography>
                )}
              </Box>
              <Typography sx={summaryStyles.lineAmount}>
                {money(Number(line.amount || 0) / 100)}
              </Typography>
            </Box>
          );
        })}

        {taxAmount > 0 && (
          <Box sx={summaryStyles.line}>
            <Typography sx={summaryStyles.lineTitle}>
              Tax ({customer?.address?.country})
            </Typography>
            <Typography sx={summaryStyles.lineAmount}>
              {money(taxAmount)}
            </Typography>
          </Box>
        )}

        <Divider sx={summaryStyles.divider} />

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            pt: 1.75,
          }}
        >
          <Typography sx={summaryStyles.totalLabel}>Total due today</Typography>
          <Box sx={{ textAlign: "right" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "flex-end",
              }}
            >
              <Box component="span" sx={summaryStyles.totalCurrencyPrefix}>
                $
              </Box>
              <Box component="span" sx={summaryStyles.totalAmount}>
                {total.toFixed(2)}
              </Box>
              <Box component="span" sx={summaryStyles.totalCurrencySuffix}>
                {invoice?.currency || "USD"}
              </Box>
            </Box>
            {saved > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "flex-end",
                  mt: 1,
                }}
              >
                <Typography sx={summaryStyles.wasPrice}>
                  {money(wasPrice)}
                </Typography>
                <Box sx={summaryStyles.saveBadge}>Save {money(saved)}</Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2.25,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            fontSize: 12.5,
            lineHeight: 1.55,
            color: colors.secondary,
          }}
        >
          <Box
            component="i"
            className="fa-regular fa-arrows-rotate"
            sx={{ color: colors.primaryDeep, mt: "2px" }}
          />
          <span>
            One-time offer: pay for 2 months now, get your 3rd free. After these
            3 months, your plan switches to monthly billing at{" "}
            {money(monthlyBase)}/mo. Cancel anytime.
          </span>
        </Box>

        <Box
          sx={{
            mt: 1.75,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            fontSize: 12.5,
            fontWeight: 600,
            color: colors.primaryDeep,
          }}
        >
          <Box component="i" className="fa-solid fa-clock" /> Offer ends{" "}
          {offerEndsLabel}
        </Box>
      </Box>

      <Box sx={{ mt: 4 }}>
        <CrossSells />
      </Box>
    </Box>
  );
};

export default B2GOCheckoutSummary;
